import type UserContext from "./userContext";
import { computed, observable, runInAction } from "mobx";
import type { INotificationService, ILocalizationService } from "@emanprague/shared-services";
import { SeverityLevel } from "@emanprague/shared-services";
import type EtsDeclarationRepository from "repositories/etsDeclarationRepository";
import type EtsSupplyPointListItem from "entities/etsSupplyPointListItem";
import type EnumsService from "./enumsService";
import PartnerType from "models/partnerType";
import type EtsDeclarationFormData from "entities/etsDeclarationFormData";
import { bound } from "@frui.ts/helpers";

export default class EtsSupplyPointsService {
  constructor(
    private userContext: UserContext,
    private notificationService: INotificationService,
    private repository: EtsDeclarationRepository,
    private localization: ILocalizationService,
    private enumsService: EnumsService
  ) {}

  @observable.ref etsSupplyPoints: EtsSupplyPointListItem[];
  @observable availableYears: string[];

  async loadEtsFormData() {
    if (this.etsSupplyPoints != undefined || !this.isCompany) {
      return;
    }

    const partnerId = this.userContext.activePartnerId;
    if (!partnerId) {
      return;
    }

    const response = await this.repository.getSupplyPoints(partnerId);
    if (response.success) {
      runInAction(() => {
        this.etsSupplyPoints = response.payload;

        const allYears = this.etsSupplyPoints.flatMap(sp => sp.availableEtsYears);
        this.availableYears = Array.from(new Set(allYears)).sort();
      });
    } else {
      runInAction(() => {
        this.etsSupplyPoints = [];
        this.availableYears = [];
      });
    }
  }

  async sendEtsDeclarationForm(data: EtsDeclarationFormData) {
    if (!this.partner) {
      return;
    }

    const response = await this.repository.sendForm(this.partner.id, data);
    if (response.success && response.payload.redirectUri) {
      this.notificationService.addNotification(this.translate("success"), SeverityLevel.success);
      return response.payload.redirectUri;
    } else {
      this.notificationService.addNotification(this.translate("error"), SeverityLevel.critical);
      return false;
    }
  }

  @computed
  get isEtsAvailable() {
    return this.isCompany && this.etsSupplyPoints && this.etsSupplyPoints.length > 0;
  }

  get isCompany() {
    if (!this.partner) {
      return false;
    }

    return this.enumsService.getPartnerType(this.partner.partnerTypeId) === PartnerType.Business;
  }

  get partner() {
    return this.userContext.activePartner;
  }

  @bound translateGeneral(code: string) {
    return this.localization.translateGeneral(`general.${code}`);
  }

  @bound translate(key: string) {
    return this.localization.translateGeneral(`ets_declaration_form.${key}`);
  }
}
